import { ReactNode, useRef, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import type PaymentMethod from '@alltrails/amplitude/enums/PaymentMethod';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import useRequestCountryCode from '@alltrails/context/hooks/useRequestCountryCode';
import useSetUser from '@alltrails/context/hooks/useSetUser';
import useUser from '@alltrails/context/hooks/useUser';
import Typography from '@alltrails/core/components/Typography';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import type SubscriptionsNewResponse from '@alltrails/core/types/SubscriptionsNewResponse';
import { useModalAriaLabelledBy } from '@alltrails/modal/components/Modal/ModalAriaLabelledByContext';
import CheckoutBase from '@alltrails/payment/components/CheckoutBase';
import PaymentFinePrint from '@alltrails/payment/components/PaymentFinePrint/PaymentFinePrint';
import ThreeDSecureContainer, {
  hideThreeDSecureContainer,
  showThreeDSecureContainer
} from '@alltrails/payment/components/ThreeDSecureContainer/ThreeDSecureContainer';
import type PaymentTokenPayload from '@alltrails/payment/types/PaymentTokenPayload';
import type ProResponse from '@alltrails/payment/types/ProResponse';
import createSubscription from '@alltrails/payment/utils/createSubscription';
import createSubscriptionUpgrade from '@alltrails/payment/utils/createSubscriptionUpgrade';
import getPlusCheckoutTrialDurationText from '@alltrails/payment/utils/getPlusCheckoutTrialDurationText';
import getPurchaseScreenTypePlan from '@alltrails/payment/utils/getPurchaseScreenTypePlan';
import handlePaymentFormSuccess from '@alltrails/payment/utils/handlePaymentFormSuccess';
import performInitialSubscriptionPurchase from '@alltrails/payment/utils/performInitialSubscriptionPurchase';
import performSubscriptionUpgrade from '@alltrails/payment/utils/performSubscriptionUpgrade';
import { getDiscountPercentage, getPriceTotal, isTrial } from '@alltrails/payment/utils/plan';
import type CountryCode from '@alltrails/types/CountryCode';
import type Coupon from '@alltrails/types/Coupon';
import type CurrencyCode from '@alltrails/types/CurrencyCode';
import type { Plan } from '@alltrails/types/Plan';
import type { SubscriptionTier } from '@alltrails/types/SubscriptionTier';
import styles from './CheckoutModal.module.scss';

function promotionFinePrint(couponCode: string) {
  if (couponCode.startsWith('tmt24')) {
    return (
      <Typography color="secondary" variant="paragraph100" component="div">
        Limited-time offer; qualifying wireless plan required. See T-Mobile Tuesdays in T Life app for details.
      </Typography>
    );
  }

  return null;
}

type CheckoutModalBaseProps = {
  ariaLabelledBy: string;
  billedHeading?: ReactNode;
  billedSubheading?: ReactNode;
  billedSubheadingRight?: ReactNode;
  billedToday: ReactNode;
  billingCountry: CountryCode;
  buttonText: ReactNode;
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  heading: ReactNode;
  plan: Plan;
  setBillingCountry: (country: CountryCode) => void;
  setCurrencyCode: (currencyCode: CurrencyCode) => void;
  setCoupon?: (coupon: Coupon) => void;
  setPlanToAnnual: () => void;
  subheading?: ReactNode;
  userId: number;
  setNonTrialPlan: (plan: Plan) => void;
  setPlan: (plan: Plan) => void;
  navigateToWelcome: () => void;
  onSuccess?: () => void;
  subscriptionTier: SubscriptionTier;
  purchaseTier: PurchaseTier;
  trigger: UpsellTrigger;
  subscriptionsNewResponse: SubscriptionsNewResponse;
};

function CheckoutModalBase({
  ariaLabelledBy,
  billedHeading,
  billedSubheading,
  billedSubheadingRight,
  billedToday,
  billingCountry,
  buttonText,
  coupon,
  currencyCode,
  heading,
  navigateToWelcome,
  onSuccess,
  plan,
  purchaseTier,
  setBillingCountry,
  setCoupon,
  setCurrencyCode,
  setNonTrialPlan,
  setPlan,
  setPlanToAnnual,
  subheading,
  subscriptionTier,
  trigger,
  userId,
  subscriptionsNewResponse
}: CheckoutModalBaseProps) {
  const setUser = useSetUser();
  const user = useUser()!;
  const threeDSecureContainerRef = useRef<HTMLDivElement>(null);

  const canUpgradeExistingSubscription = user.canUpgradeExistingSubscription;

  const finishSuccessfulPurchase = async (apiProResponse: ProResponse, paymentMethod: PaymentMethod) => {
    setUser(apiProResponse.user!);

    handlePaymentFormSuccess({
      apiProResponse,
      coupon,
      currencyCode,
      paymentMethod,
      plan,
      trigger,
      userId,
      webProPurchaseLocation: 'modal'
    });

    await onSuccess?.();
    navigateToWelcome();
  };

  const purchaseWithPaymentMethod = async (paymentMethod: PaymentMethod, paymentFields: PaymentTokenPayload) => {
    const container = threeDSecureContainerRef.current!;
    const analyticsInfo = {
      payment_method: paymentMethod,
      product_id: plan.recurlyPlanCode,
      purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon),
      purchase_type: canUpgradeExistingSubscription ? 'upgrade' : 'initial_subscription',
      upsell_trigger: trigger
    };

    if (canUpgradeExistingSubscription) {
      const response = await performSubscriptionUpgrade({
        analyticsInfo,
        body: {
          ...(coupon && { coupon_code: coupon.code }),
          ...paymentFields,
          price_code: plan.priceCode,
          uid: userId,
          upsell_trigger: trigger
        },
        container,
        onThreeDSStart: () => showThreeDSecureContainer(container),
        onThreeDSEnd: () => hideThreeDSecureContainer(container)
      });
      await finishSuccessfulPurchase(response, paymentMethod);
      return;
    }

    const response = await performInitialSubscriptionPurchase({
      analyticsInfo,
      body: {
        billing_country: billingCountry,
        ...paymentFields,
        price_code: plan.priceCode,
        uid: userId,
        upsell_trigger: trigger,
        user: {
          user_entered_coupon_code: coupon ? coupon.code : undefined
        }
      },
      container,
      onThreeDSStart: () => showThreeDSecureContainer(container),
      onThreeDSEnd: () => hideThreeDSecureContainer(container)
    });

    await finishSuccessfulPurchase(response, paymentMethod);
  };

  return (
    <>
      <ThreeDSecureContainer containerRef={threeDSecureContainerRef} />
      <CheckoutBase
        ariaLabelledBy={ariaLabelledBy}
        billedHeading={billedHeading}
        billedSubheading={billedSubheading}
        billedSubheadingRight={billedSubheadingRight}
        billedToday={billedToday}
        billingCountry={billingCountry}
        buttonText={buttonText}
        canUpgradeExistingSubscription={canUpgradeExistingSubscription}
        coupon={coupon}
        currencyCode={currencyCode}
        finePrint={
          <PaymentFinePrint
            billingCountry={billingCountry}
            coupon={coupon}
            currencyCode={currencyCode}
            isCouponApplied={Boolean(coupon)}
            plan={plan}
            subscriptionTier={subscriptionTier}
          />
        }
        {...(coupon ? { promoText: promotionFinePrint(coupon.code) } : {})}
        heading={heading}
        isUpgrade={user.subscriptionTier === 'plus' && purchaseTier === 'peak'}
        onBillingCountryChange={(value: CountryCode, response) => {
          setBillingCountry(value);
          setCurrencyCode(response.currencyCode);
          setNonTrialPlan(response.nonTrial);

          // similar logic in pro_controllers_helper.rb for initial load wonder if we can consolidate in one spot?
          if (!coupon && (!user || user.isPromoEligible)) {
            setPlan(response.trial);
          } else {
            setPlan(response.nonTrial);
          }
        }}
        onPaymentToken={async ({ payment_method_type, recaptcha, token, tokenization_source }) => {
          await purchaseWithPaymentMethod(payment_method_type === 'card' ? 'credit_card' : payment_method_type, {
            payment_method_type,
            recaptcha,
            token,
            tokenization_source
          });
        }}
        onSubmitGiftCardRedemption={async ({ recaptcha }) => {
          if (canUpgradeExistingSubscription) {
            const response = await createSubscriptionUpgrade({
              ...(coupon && { coupon_code: coupon.code }),
              recaptcha,
              price_code: plan.priceCode,
              uid: userId,
              upsell_trigger: trigger
            });
            await finishSuccessfulPurchase(response, 'credit_card');
            return;
          }

          const response = await createSubscription({
            billing_country: billingCountry,
            recaptcha,
            price_code: plan.priceCode,
            uid: userId,
            upsell_trigger: trigger,
            user: {
              user_entered_coupon_code: coupon ? coupon.code : undefined
            }
          });
          await finishSuccessfulPurchase(response, 'credit_card');
        }}
        plan={plan}
        purchaseTier={purchaseTier}
        isCheckoutModal
        setCoupon={setCoupon}
        setPlanToAnnual={setPlanToAnnual}
        subheading={subheading}
        subscription={subscriptionsNewResponse.subscription}
        trigger={trigger}
        webProPurchaseLocation="modal"
        whenExpired={subscriptionsNewResponse.whenExpired}
      />
    </>
  );
}

type CheckoutProps = {
  initialNonTrialPlan: Plan;
  initialPlan: Plan;
  navigateToWelcome: () => void;
  onSuccess?: () => void;
  purchaseTier: PurchaseTier;
  subscriptionTier: SubscriptionTier;
  trigger: UpsellTrigger;
  userId: number;
  subscriptionsNewResponse: SubscriptionsNewResponse;
};

// Since this is an experiment, it is easier to copy this code from PlusCheckout than trying to create a PlusCheckoutBase component
// https://www.figma.com/design/dOj6Te4I0TVguYevn6Fj5g/Web-Checkout-Improvements?node-id=268-59615&t=aGFQDG4oH7HNoMzN-0
export default function CheckoutModal({
  initialNonTrialPlan,
  initialPlan,
  navigateToWelcome,
  onSuccess,
  purchaseTier,
  subscriptionTier,
  trigger,
  userId,
  subscriptionsNewResponse
}: CheckoutProps) {
  const ariaLabelledBy = useModalAriaLabelledBy();
  const [billingCountry, setBillingCountry] = useState(subscriptionsNewResponse.countryCode);
  const [coupon, setCoupon] = useState<Coupon>();
  const [currencyCode, setCurrencyCode] = useState(subscriptionsNewResponse.currencyCode);
  const [nonTrialPlan, setNonTrialPlan] = useState(initialNonTrialPlan);

  const [plan, setPlan] = useState(initialPlan);

  const requestCountryCode = useRequestCountryCode();
  const showPlusTaxesText = billingCountry === 'US' || billingCountry === 'CA';

  function setPlanToAnnual() {
    setPlan(nonTrialPlan);
  }

  const annualCost = <FormattedNumber currency={currencyCode} style="currency" value={plan.priceTotal} />;

  // Mobile: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c24f591c20f2d3669a8ea
  // Desktop: https://www.figma.com/file/vJ30CAAm8qdbDO0j5C4Ce7/Web-Pro-Purchase-Flow?node-id=685%3A35997
  // Desktop: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c238b04a41125e91d0422

  if (coupon && coupon.type !== 'FreeTrialCoupon') {
    const percent = getDiscountPercentage(plan, coupon) / 100;

    function getHeading() {
      return purchaseTier === 'peak' ? (
        <FormattedMessage defaultMessage="Save {percent} on Peak" values={{ percent: <FormattedNumber style="percent" value={percent} /> }} />
      ) : (
        <FormattedMessage defaultMessage="Save {percent} on Plus" values={{ percent: <FormattedNumber style="percent" value={percent} /> }} />
      );
    }

    return (
      <CheckoutModalBase
        ariaLabelledBy={ariaLabelledBy}
        billedSubheading={<FormattedMessage defaultMessage="Discount" />}
        billedSubheadingRight={<FormattedMessage defaultMessage="Save {percent, number, ::percent}" values={{ percent }} />}
        billedToday={
          <>
            <s className={styles.s}>{annualCost}</s> <FormattedNumber currency={currencyCode} style="currency" value={getPriceTotal(plan, coupon)} />
          </>
        }
        billingCountry={billingCountry}
        buttonText={<FormattedMessage defaultMessage="Save today" />}
        coupon={coupon}
        currencyCode={currencyCode}
        heading={getHeading()}
        navigateToWelcome={navigateToWelcome}
        onSuccess={onSuccess}
        plan={plan} // this is always non trial so we could always pass nonTrial
        purchaseTier={purchaseTier}
        setBillingCountry={setBillingCountry}
        setCoupon={setCoupon}
        setCurrencyCode={setCurrencyCode}
        setNonTrialPlan={setNonTrialPlan}
        setPlan={setPlan}
        setPlanToAnnual={setPlanToAnnual}
        subscriptionTier={subscriptionTier}
        trigger={trigger}
        userId={userId}
        subscriptionsNewResponse={subscriptionsNewResponse}
      />
    );
  }

  if (isTrial(plan, coupon)) {
    const trialDurationText = getPlusCheckoutTrialDurationText({
      purchaseTier,
      couponTrialDuration: coupon?.freeTrialDuration
    });

    return (
      <CheckoutModalBase
        ariaLabelledBy={ariaLabelledBy}
        billedSubheading={<FormattedMessage defaultMessage="Billed after trial" />}
        billedSubheadingRight={
          <FormattedMessage
            defaultMessage="{annualCost} annually{plusTaxes, select, true { + tax} other {}}"
            values={{ annualCost, plusTaxes: showPlusTaxesText }}
          />
        }
        billedToday={<FormattedNumber currency={currencyCode} style="currency" value={0} />}
        billingCountry={billingCountry}
        buttonText={<FormattedMessage defaultMessage="Start your free trial" />}
        coupon={coupon}
        currencyCode={currencyCode}
        heading={trialDurationText}
        navigateToWelcome={navigateToWelcome}
        onSuccess={onSuccess}
        plan={plan}
        purchaseTier={purchaseTier}
        setBillingCountry={setBillingCountry}
        setCoupon={setCoupon}
        setCurrencyCode={setCurrencyCode}
        setNonTrialPlan={setNonTrialPlan}
        setPlan={setPlan}
        setPlanToAnnual={setPlanToAnnual}
        subheading={
          requestCountryCode === 'US' ? (
            <FormattedMessage defaultMessage="Then just {annualCost} a year after your trial." values={{ annualCost }} />
          ) : (
            <FormattedMessage
              defaultMessage="Then just {annualCost} a year after your trial. No commitment, cancel anytime."
              values={{ annualCost }}
            />
          )
        }
        subscriptionTier={subscriptionTier}
        trigger={trigger}
        userId={userId}
        subscriptionsNewResponse={subscriptionsNewResponse}
      />
    );
  }

  function getHeading() {
    return purchaseTier === 'peak' ? (
      <FormattedMessage defaultMessage="Explore to the fullest with Peak" />
    ) : (
      <FormattedMessage defaultMessage="Adventure offline with Plus" />
    );
  }

  // Desktop: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c23891dd05a2a732656a0
  // Mobile: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c24a858c4c42d0bd73f80
  return (
    <CheckoutModalBase
      ariaLabelledBy={ariaLabelledBy}
      billedToday={
        showPlusTaxesText ? (
          <FormattedMessage
            defaultMessage="{annualCost} annually{plusTaxes, select, true { + tax} other {}}"
            values={{ annualCost, plusTaxes: showPlusTaxesText }}
          />
        ) : (
          annualCost
        )
      }
      billingCountry={billingCountry}
      buttonText={<FormattedMessage defaultMessage="Upgrade" />}
      currencyCode={currencyCode}
      heading={getHeading()}
      navigateToWelcome={navigateToWelcome}
      onSuccess={onSuccess}
      plan={plan} // this is always non trial so we could always pass nonTrial
      purchaseTier={purchaseTier}
      setBillingCountry={setBillingCountry}
      setCoupon={setCoupon}
      setCurrencyCode={setCurrencyCode}
      setNonTrialPlan={setNonTrialPlan}
      setPlan={setPlan}
      setPlanToAnnual={setPlanToAnnual}
      subscriptionTier={subscriptionTier}
      trigger={trigger}
      userId={userId}
      subscriptionsNewResponse={subscriptionsNewResponse}
    />
  );
}
